/**
Calls the BLOY storefront API through Shopify's App Proxy (/apps/bloy/*, same-origin)
 * instead of BloyConfigs.bloyApiUrl directly. BLOY's storefront guards now only trust the
 * customer id from Shopify's signed logged_in_customer_id, so a direct cross-origin call
 * to bloyApiUrl gets rejected with 403 Forbidden regardless of any token sent.
 */
(function () {
  'use strict';

  var GID_VARIANT = 'gid://shopify/ProductVariant/';
  var APP_PROXY_PREFIX = '/apps/bloy';
  function numericId(gid) {
    var s = String(gid || '');
    return s.indexOf('/') > -1 ? s.split('/').pop() : s;
  }

  function interpolate(tpl, value) {
    return String(tpl == null ? '' : tpl).replace(/\[\[\s*points\s*\]\]/gi, value);
  }

  function formatPoints(n) {
    return Number(n || 0).toLocaleString();
  }

  /** BLOY globals are injected by Liquid and guarded everywhere in BLOY's own code. */
  function bloy() {
    if (typeof window.BloyConfigs === 'undefined' || !window.BloyConfigs) return null;
    if (!window.BloyConfigs.bloyApiUrl || !window.BloyConfigs.shop_id) return null;
    return window.BloyConfigs;
  }

  function bloyUser() {
    if (typeof window.BloyUser === 'undefined' || !window.BloyUser) return null;
    if (!window.BloyUser.id || !window.BloyUser.token) return null;
    return window.BloyUser;
  }

  function locale() {
    try {
      return (window.BloyCurrentLocale && window.BloyCurrentLocale.language) || 'en';
    } catch (e) {
      return 'en';
    }
  }

  function api(cfg, path, body, token) {
    var headers = { 'Content-type': 'application/json' };
    if (token) headers['x-bloy-token'] = token;
       // Same-origin call through Shopify's App Proxy — Shopify verifies the request and
    // appends its own signed logged_in_customer_id before forwarding to the BLOY backend.
    return fetch(APP_PROXY_PREFIX + path, {
       method: 'POST',
       headers: headers,
       body: JSON.stringify(body)
     }).then(function (r) {
      return r.json().catch(function () {
        return null;
      });
    });
  }

  function Section(root) {
    this.root = root;
    this.sectionId = root.id.replace('shopify-section-', '');

    var configEl = document.querySelector(
      '[data-wub-rewards-config="' + this.sectionId + '"]'
    );
    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      this.config = { labels: {} };
    }
    this.labels = this.config.labels || {};

    this.cards = [].slice.call(root.querySelectorAll('[data-reward-card]'));
    this.modal = root.querySelector('[data-reward-modal]');
    this.balanceEl = root.querySelector('[data-reward-balance]');

    this.points = 0;
    this.rulesByProduct = {};
    this.rulesByAmount = {};
    this.rulesById = {};
    this.active = null;
    this.busy = false;

    this.bindModal();
    this.bindCards();
    this.load();
  }

  Section.prototype.el = function (name) {
    return this.modal.querySelector('[data-modal-' + name + ']');
  };

  Section.prototype.bindCards = function () {
    var self = this;
    this.cards.forEach(function (card) {
      var btn = card.querySelector('[data-reward-trigger]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        if (!btn.disabled) self.open(card);
      });
    });
  };

  Section.prototype.bindModal = function () {
    var self = this;
    [].slice.call(this.modal.querySelectorAll('[data-modal-close]')).forEach(function (b) {
      b.addEventListener('click', function () {
        self.close();
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !self.modal.hidden) self.close();
    });
    var redeem = this.el('redeem');
    if (redeem) {
      redeem.addEventListener('click', function () {
        self.redeem();
      });
    }
  };

  /* ---------------------------------------------------------------- load ---- */

  Section.prototype.load = function () {
    var cfg = bloy();
    var user = bloyUser();

    if (!cfg) {
      this.fallback(this.labels.unavailable);
      return;
    }
    if (!user) {
      // Not logged in (or BLOY hasn't minted a token) — points are unknowable.
      this.fallback(this.labels.login);
      return;
    }

    var self = this;
    api(cfg, '/redeemFn', {
      cid: String(user.id),
      shop: cfg.shop_id,
      language: locale()
    })
      .then(function (json) {
        var list = (json && json.payload) || [];
        list.forEach(function (entry) {
          var rule = entry && entry.rule;
          if (!rule) return;
          self.rulesById[String(rule._id)] = rule;

          if (rule.type === 'free_product') {
            // applyVariants entries are products, each carrying its variant list.
            (rule.applyVariants || []).forEach(function (p) {
              self.rulesByProduct[numericId(p && p.id)] = rule;
            });
          } else if (rule.type === 'amount') {
            // Amount coupons carry no product/variant — key them by their point cost
            // (rewardValue). Unique among coupons here (1000/2000/4000/6000), so a card
            // whose entered points equals a rule's rewardValue binds to that rule.
            self.rulesByAmount[String(rule.rewardValue)] = rule;
          }
        });
        return self.loadBalance(cfg, user);
      })
      .then(function () {
        self.render();
      })
      .catch(function () {
        self.fallback(self.labels.unavailable);
      });
  };

  Section.prototype.loadBalance = function (cfg, user) {
    var self = this;
    // /auth needs dID to be valid base64 JSON — it is atob+JSON.parse'd unguarded.
    var dID = window.btoa(
      JSON.stringify({ uid: 'wub-member-rewards', renderer: 'theme' })
    );
    return api(
      cfg,
      '/auth',
      {
        email: user.email || '',
        cid: String(user.id),
        shop: cfg.shop_id,
        dID: dID
      },
      user.token
    ).then(function (json) {
      var payload = json && json.payload;
      self.points = (payload && payload.points) || 0;
    });
  };

  /* -------------------------------------------------------------- render ---- */

  /** The points number the merchant entered on the card — parsed from the displayed
   *  points field (e.g. "1,000 Points" -> 1000), falling back to the numeric field.
   *  Used to bind amount coupons to their BLOY rule by rewardValue. */
  Section.prototype.cardPoints = function (card) {
    var el = card.querySelector('[data-card-points]');
    var digits = el ? String(el.textContent).replace(/[^\d]/g, '') : '';
    if (digits) return Number(digits);
    return Number(card.getAttribute('data-fallback-points')) || 0;
  };

  Section.prototype.ruleFor = function (card) {
    var forced = card.getAttribute('data-reward-id');
    if (forced && this.rulesById[forced]) return this.rulesById[forced];

    // Free products bind by their Shopify product id.
    var byProduct = this.rulesByProduct[card.getAttribute('data-product-id')];
    if (byProduct) return byProduct;

    // Amount coupons have no product — match the entered points to a rule's rewardValue.
    var pts = this.cardPoints(card);
    if (pts && this.rulesByAmount[String(pts)]) return this.rulesByAmount[String(pts)];

    return null;
  };

  /** Cost always comes from BLOY; Liquid's value is a pre-API placeholder only. */
  Section.prototype.costFor = function (card, rule) {
    if (rule && typeof rule.rewardValue === 'number') return rule.rewardValue;
    return Number(card.getAttribute('data-fallback-points')) || 0;
  };

  Section.prototype.render = function () {
    var self = this;

    if (this.balanceEl && this.labels.balance) {
      this.balanceEl.textContent = interpolate(
        this.labels.balance,
        formatPoints(this.points)
      );
      this.balanceEl.hidden = false;
    }

    this.cards.forEach(function (card) {
      var rule = self.ruleFor(card);
      var btn = card.querySelector('[data-reward-trigger]');
      var cost = self.costFor(card, rule);

      self.paintPoints(card, cost);

      if (!btn) return;
      if (!rule) {
        // No BLOY rule for this product — nothing to redeem against.
        btn.disabled = true;
        btn.textContent = self.labels.unavailable;
        return;
      }
      // Grey out cards the customer cannot afford.
      btn.disabled = self.points < cost;
      btn.textContent = self.labels.redeem;
    });
  };

  Section.prototype.paintPoints = function (card, cost) {
    var el = card.querySelector('[data-card-points]');
    if (!el) return;
    var unit = card.getAttribute('data-points-unit') || this.labels.pointsUnit || '';
    el.innerHTML = '';
    el.appendChild(document.createTextNode(formatPoints(cost) + ' '));
    var span = document.createElement('span');
    span.className = 'point-unit';
    span.textContent = unit;
    el.appendChild(span);
  };

  /** API unreachable / not logged in: keep Liquid values, disable every button. */
  Section.prototype.fallback = function (label) {
    this.cards.forEach(function (card) {
      var btn = card.querySelector('[data-reward-trigger]');
      if (!btn) return;
      btn.disabled = true;
      btn.textContent = label || '';
    });
  };

  /* --------------------------------------------------------------- modal ---- */

  Section.prototype.open = function (card) {
    var rule = this.ruleFor(card);
    if (!rule) return;

    this.active = { card: card, rule: rule, cost: this.costFor(card, rule) };
    this.busy = false;

    var name = card.querySelector('.wub-member-rewards__name');
    var img = card.querySelector('.wub-member-rewards__image');

    this.el('title').textContent = name ? name.textContent.trim() : '';
    this.el('points').textContent =
      formatPoints(this.active.cost) +
      ' ' +
      (card.getAttribute('data-points-unit') || this.labels.pointsUnit || '');

    var main = this.el('image');
    if (main) {
      main.src = img ? img.getAttribute('src') : '';
      main.alt = this.el('title').textContent;
    }
    this.buildThumbs(card);

    var cart = this.el('cart');
    if (cart) cart.hidden = true;

    this.setMessage('', '');
    this.applyAffordability();

    this.modal.hidden = false;
    document.body.style.overflow = 'hidden';
  };

  Section.prototype.buildThumbs = function (card) {
    var wrap = this.el('thumbs');
    if (!wrap) return;
    wrap.innerHTML = '';

    var img = card.querySelector('.wub-member-rewards__image');
    if (!img) return;

    var self = this;
    var srcs = [img.getAttribute('src')];
    srcs.forEach(function (src, i) {
      if (!src) return;
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'wub-member-rewards__thumb';
      b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      var t = document.createElement('img');
      t.src = src;
      t.alt = '';
      t.width = 72;
      t.height = 72;
      b.appendChild(t);
      b.addEventListener('click', function () {
        self.el('image').src = src;
        [].slice.call(wrap.children).forEach(function (c) {
          c.setAttribute('aria-current', c === b ? 'true' : 'false');
        });
      });
      wrap.appendChild(b);
    });
  };

  /** Requirement: disabled + grey when the customer can't afford the reward. */
  Section.prototype.applyAffordability = function () {
    var btn = this.el('redeem');
    if (!btn || !this.active) return;

    var short = this.active.cost - this.points;
    if (short > 0) {
      btn.disabled = true;
      btn.textContent = this.labels.redeem;
      this.setMessage(interpolate(this.labels.insufficient, formatPoints(short)), 'error');
    } else {
      btn.disabled = false;
      btn.textContent = this.labels.redeem;
    }
  };

  Section.prototype.setMessage = function (text, state) {
    var el = this.el('message');
    if (!el) return;
    el.textContent = text || '';
    if (state) el.setAttribute('data-state', state);
    else el.removeAttribute('data-state');
  };

  Section.prototype.close = function () {
    if (this.busy) return; // never close mid-redeem
    this.modal.hidden = true;
    document.body.style.overflow = '';
    this.active = null;
  };

  /* -------------------------------------------------------------- redeem ---- */

  Section.prototype.redeem = function () {
    if (this.busy || !this.active) return;

    var cfg = bloy();
    var user = bloyUser();
    if (!cfg || !user) {
      this.setMessage(this.labels.login, 'error');
      return;
    }

    var rule = this.active.rule;
    var cost = this.active.cost;
    var card = this.active.card;

    if (cost - this.points > 0) return; // guarded by applyAffordability too

    // Only free_product needs a variant to add to the cart; coupons issue a code only.
    var isFree = rule.type === 'free_product';
    var variantGid = isFree ? this.variantGidFor(card, rule) : null;
    if (isFree && !variantGid) {
      this.setMessage(this.labels.error, 'error');
      return;
    }

    var btn = this.el('redeem');
    // Latch immediately: points are deducted server-side before the cart add and
    // there is no rollback, so a double submit would charge twice.
    this.busy = true;
    btn.disabled = true;
    btn.textContent = this.labels.redeeming;
    this.setMessage('', '');

    var self = this;
    api(
      cfg,
      '/redeemFn/purchase/' + rule._id,
      {
        shop: cfg.shop_id,
        cid: String(user.id),
        cost: Number(cost),
        language: locale(),
        source: 'page',
        variantId: variantGid || undefined,
        currencyActive:
          (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
          undefined
      },
      user.token
    )
      .then(function (json) {
        if (!json || json.message !== 'OK' || !json.payload) {
          return self.failRedeem(json);
        }
        // Points are already spent at this point.
        self.points = Math.max(0, self.points - cost);
        if (isFree) {
          return self.applyReward(numericId(variantGid), json.payload);
        }
        // Coupon (amount / etc.): just activate the code, no cart, no redirect.
        return self.applyDiscount(json.payload);
      })
      .catch(function () {
        self.failRedeem(null);
      });
  };

  Section.prototype.variantGidFor = function (card, rule) {
    var override = card.getAttribute('data-variant-id');
    if (override) return GID_VARIANT + numericId(override);

    var products = rule.applyVariants || [];
    var pid = card.getAttribute('data-product-id');
    for (var i = 0; i < products.length; i++) {
      var p = products[i];
      if (numericId(p.id) !== pid) continue;
      var vs = p.variants || [];
      if (vs.length && vs[0].id) return vs[0].id;
    }
    // Fall back to the rule's first variant so a product-id mismatch still redeems.
    for (var j = 0; j < products.length; j++) {
      var vv = (products[j].variants || [])[0];
      if (vv && vv.id) return vv.id;
    }
    return null;
  };

  /** Add the free variant to the cart, then attach the discount code to the session. */
  Section.prototype.applyReward = function (variantId, discountCode) {
    var self = this;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (r) {
        if (r.ok) return true;
        return r.json().then(
          function (e) {
            throw new Error((e && e.description) || 'cart');
          },
          function () {
            throw new Error('cart');
          }
        );
      })
      .then(function () {
        // Applies the BLOY discount so the free-product Function fires at checkout.
        // return fetch('/discount/' + encodeURIComponent(discountCode)).catch(function () {});
        var checkoutUrl = root + 'checkout';
        window.location.href =
          root + 'discount/' + encodeURIComponent(discountCode) +
          '?redirect=' + encodeURIComponent(checkoutUrl);
      })
      .then(function () {
        self.succeed();
      })
      .catch(function () {
        // Points were spent and the code was issued, but the cart add failed —
        // most likely the redemption-only variant is out of stock.
        self.busy = false;
        var btn = self.el('redeem');
        btn.disabled = true;
        btn.textContent = self.labels.redeem;
        self.setMessage(self.labels.soldOut, 'error');
        self.refreshBalance();
      });
  };

  /**
   * Coupon reward (type 'amount', and any other non-product code): activate the issued
   * discount code for the session so it auto-applies at checkout. No cart add, no redirect.
   * Best-effort — the code is already granted server-side, so a failed fetch still counts
   * as success (we just don't pre-activate it; the customer can still enter it manually).
   */
  Section.prototype.applyDiscount = function (discountCode) {
    var self = this;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'discount/' + encodeURIComponent(discountCode))
      .catch(function () {})
      .then(function () {
        self.succeed();
      });
  };

  Section.prototype.succeed = function () {
    this.busy = false;

    var btn = this.el('redeem');
    btn.hidden = true;

    var cart = this.el('cart');
    if (cart) {
      cart.href = this.config.cartUrl || '/cart';
      cart.hidden = false;
    }
    this.setMessage(this.labels.success, 'success');
    this.refreshBalance();
  };

  Section.prototype.failRedeem = function (json) {
    this.busy = false;
    var btn = this.el('redeem');
    btn.disabled = false;
    btn.textContent = this.labels.redeem;

    var msg = this.labels.error;
    if (json && json.message === 'REDEEM_LIMIT_REACHED') {
      msg = this.labels.unavailable;
    } else if (json && json.message === 'ORDER_LIMIT_REACHED') {
      msg = this.labels.unavailable;
    }
    this.setMessage(msg, 'error');
  };

  /** Re-paint balance + card affordability after a successful spend. */
  Section.prototype.refreshBalance = function () {
    var self = this;
    // Re-grey every card against the new balance, but leave the open modal's
    // redeem button alone once it has succeeded (it is hidden behind Go to Cart).
    this.cards.forEach(function (card) {
      var rule = self.ruleFor(card);
      var btn = card.querySelector('[data-reward-trigger]');
      if (!btn || !rule) return;
      btn.disabled = self.points < self.costFor(card, rule);
    });

    if (this.balanceEl && this.labels.balance) {
      this.balanceEl.textContent = interpolate(
        this.labels.balance,
        formatPoints(this.points)
      );
    }
    var btn = this.el('redeem');
    if (btn && btn.hidden) return;
    if (this.active) this.applyAffordability();
  };

  /* ----------------------------------------------------------------- init ---- */

  function init() {
    [].slice
      .call(document.querySelectorAll('[data-wub-rewards-config]'))
      .forEach(function (cfgEl) {
        var id = cfgEl.getAttribute('data-wub-rewards-config');
        var root = document.getElementById('shopify-section-' + id);
        if (root && !root.__wubRewards) {
          root.__wubRewards = new Section(root);
        }
      });
  }

  // This theme can inject/replace the section after the deferred script runs, so
  // init() is idempotent (guarded by root.__wubRewards) and re-run on every signal
  // rather than relying on readyState alone.
  init();
  document.addEventListener('DOMContentLoaded', init);
  window.addEventListener('load', init);
  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);

  if (typeof MutationObserver !== 'undefined') {
    var pending = new MutationObserver(function () {
      if (document.querySelector('[data-wub-rewards-config]')) init();
    });
    pending.observe(document.documentElement, { childList: true, subtree: true });
    // Stop watching once the page has settled; init() stays bound to the events above.
    window.addEventListener('load', function () {
      setTimeout(function () {
        pending.disconnect();
      }, 5000);
    });
  }
})();